import { useMemo } from 'react'
import { buoys } from '@/core/data'
import type { Key } from '@/core/i18n'
import type { IalaRegion } from '@/core/settings'
import { useSettings } from './settings'

export interface BuoyCatalogueProps {
  buoyId: string
  region: IalaRegion
  onSelect: (id: string) => void
}

/** Die Kategorie eines Seezeichens als Schlüssel im Wörterbuch. */
function categoryKey(category: string): Key {
  return `buoy.category.${category}` as Key
}

/** Seezeichen der gewählten Region. Zeichen, die in beiden Regionen gleich sind, stehen immer dabei. */
export function BuoyCatalogue({ buoyId, region, onSelect }: BuoyCatalogueProps) {
  const { t, pick } = useSettings()
  const groups = useMemo(() => {
    const hits = buoys.filter((b) => b.region === 'both' || b.region === region)
    const byCategory = new Map<string, typeof hits>()
    for (const b of hits) {
      const list = byCategory.get(b.category) ?? []
      list.push(b)
      byCategory.set(b.category, list)
    }
    return [...byCategory.entries()]
  }, [region])

  const count = groups.reduce((n, [, list]) => n + list.length, 0)

  return (
    <section className="catalogue" data-testid="buoy-catalogue">
      <div className="catalogue__head">
        <span className="catalogue__count" data-testid="buoy-count">
          {t('buoy.count', { count, region })}
        </span>
      </div>
      {groups.map(([category, list]) => (
        <div key={category} className="catalogue__group">
          <h3 className="catalogue__rule">{t(categoryKey(category))}</h3>
          <div className="catalogue__items">
            {list.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`states__item${b.id === buoyId ? ' states__item--on' : ''}`}
                data-buoy={b.id}
                onClick={() => onSelect(b.id)}
              >
                <span className="states__title">{pick(b.name)}</span>
                {b.character && <span className="states__scope">{b.character}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}
